// Adapt Google metrics onto the META chart system — PURE + tested.
//
// The Meta reporting already owns a complete charting stack (mini-chart tiles, the
// big trend chart, the shadcn chart primitives, the bucket/date domain, the style
// toggle). Rather than build a second one, this shapes Google's summary into the
// exact `TrendChartData` those components consume, so the two dashboards render
// through the same code and cannot drift apart visually or behaviourally.
//
// ⚠️ THE TWO GOOGLE RULES SURVIVE THE ADAPTATION — they are the whole reason this
// is an adapter and not a copy:
//
//  1. RATIOS ARE RECOMPUTED PER BUCKET from that bucket's summed numerator and
//     denominator, never averaged and never summed. `bucketGoogleTrend` sums only
//     the ADDITIVE totals; `computeMetrics` then derives the ratio. So a weekly
//     CTR is the week's clicks ÷ the week's impressions, not the mean of seven
//     daily CTRs.
//  2. MONEY IS PER-CURRENCY. A chart is a single series, so it can only draw one
//     currency: `currencyFor` picks it explicitly and the series is that
//     currency's own figures — never a total blended across currencies, which
//     would be a number that exists nowhere.
//
// ⚠️ An UNMEASURED bucket plots as `null` (a gap), never 0. Impression share is
// nullable by design, and a 0 would draw a line to the floor and read as "we lost
// the whole auction that day" when in fact Google reported nothing.

import { bucketIdFor, enumerateBucketDomain, type TrendGrouping } from '../reporting/chartDate';
import type { MetricTone, TrendChartData, TrendMetric } from '../reporting/trendSeries';
import { computeDelta, type HeroDelta } from '../reporting/heroCards';
import { convertGoogleMetrics } from './googleCurrency';
import { isNativeDisplay } from '../../lib/reporting/currencyConversion';
import type { FxRateSnapshot } from '../../types/fxRates';

/** The agency display currency for a render. Absent / native = per-currency. */
export interface GoogleMoneyDisplay {
  display: string;
  snapshot: FxRateSnapshot | null;
}
import {
  addToCurrency,
  computeMetrics,
  emptyTotals,
  isMoneyMetric,
  metricDef,
  metricMoney,
  metricScalar,
  type TotalsByCurrency,
} from '../../lib/googleAdsMetrics';
import type {
  GoogleAdsMetricKey,
  GoogleAdsMetrics,
  GoogleAdsSummary,
  GoogleAdsTotals,
  GoogleAdsTrendPoint,
} from '../../types/googleAdsReporting';

/** One time bucket's per-currency totals. */
export interface GoogleTrendBucket {
  bucket: string;
  byCurrency: TotalsByCurrency;
}

/**
 * Re-bucket the daily trend into daily / weekly / monthly buckets, summing ONLY
 * the additive totals. Ratios are deliberately not touched here — they are
 * recomputed from each bucket's sums at read time, which is what makes any
 * grouping automatically correct.
 */
export function bucketGoogleTrend(
  trend: readonly GoogleAdsTrendPoint[],
  grouping: TrendGrouping,
): GoogleTrendBucket[] {
  const byBucket = new Map<string, TotalsByCurrency>();
  for (const point of trend) {
    const id = bucketIdFor(point.date, grouping);
    let acc = byBucket.get(id);
    if (!acc) {
      acc = new Map();
      byBucket.set(id, acc);
    }
    for (const { currency, totals } of point.byCurrency) addToCurrency(acc, currency, totals);
  }
  return [...byBucket.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([bucket, byCurrency]) => ({ bucket, byCurrency }));
}

/**
 * The currency a money series is drawn in.
 *
 * ⚠️ A single-series chart cannot honestly show two currencies at once, and
 * summing them would invent a number. So one is chosen (the summary's first,
 * which is sorted and therefore stable) and the series is genuinely that
 * currency's figures. Non-money metrics are currency-agnostic → null.
 */
export function currencyFor(
  summary: Pick<GoogleAdsSummary, 'currencies'>,
  key: GoogleAdsMetricKey,
  money?: GoogleMoneyDisplay,
): string | null {
  if (!isMoneyMetric(key)) return null;
  // ⚠️ With a display currency the series IS that currency — the whole book
  // converted and blended, rather than one currency's own figures.
  if (money && !isNativeDisplay(money.display)) return money.display;
  return summary.currencies[0] ?? null;
}

/** One bucket's value for a metric, or null when it was not measured. */
export function bucketValue(
  byCurrency: TotalsByCurrency,
  key: GoogleAdsMetricKey,
  currency: string | null,
  money?: GoogleMoneyDisplay,
): number | null {
  // ⚠️ Converting the bucket's METRIC BLOCK (not the plotted number) is what
  // keeps a money RATIO correct per bucket: convertGoogleMetrics re-derives the
  // ratio from that bucket's converted total, rather than converting an already-
  // divided figure.
  const metrics =
    convertGoogleMetrics(computeMetrics(byCurrency), money?.display ?? '', money?.snapshot) ??
    computeMetrics(byCurrency);
  if (isMoneyMetric(key)) {
    const amounts = metricMoney(metrics, key);
    if (amounts.length === 0) return null;
    // Draw the chosen currency's own figure; never a cross-currency total.
    const picked = currency ? amounts.find((a) => a.currency === currency) : amounts[0];
    return picked ? picked.amount : null;
  }
  return metricScalar(metrics, key);
}

/**
 * Which direction is "good" for a Google metric — the delta COLOUR rule.
 *
 * ⚠️ COST IS NEUTRAL, not good-up. Spending more is neither an achievement nor a
 * problem on its own, exactly as the Meta catalogue already treats `spend`; a
 * rising cost shown in green would be a wrong commercial signal on a client's
 * scorecard. Everything the catalogue marks `lowerIsBetter` (CPC, CPM, cost per
 * conversion, both lost-IS shares) is good-down; the rest is good-up.
 *
 * ONE definition, so the trend metric shape and the widget scorecard's delta
 * cannot disagree about which way is up.
 */
export function googleMetricTone(key: GoogleAdsMetricKey): MetricTone {
  if (key === 'cost') return 'neutral';
  return metricDef(key)?.lowerIsBetter ? 'good-down' : 'good-up';
}

/**
 * The vs-previous-period change for a Google metric, using the SAME delta
 * definition as the Meta hero cards (`computeDelta`).
 *
 * ⚠️ A MONEY METRIC ONLY GETS A DELTA IN A SINGLE-CURRENCY SCOPE, and only when
 * both periods are in the SAME currency. There is no display-currency toggle on
 * this dashboard, so a mixed AUD+NZD scope has no single figure to take a
 * percentage of — summing the currencies as a "sort proxy" is fine for ordering
 * a table but would be a fabricated number here, presented as a change in the
 * client's spend. No comparable figure → no delta, which the widget then states
 * rather than leaving to read as "no change".
 *
 * Non-money metrics are currency-agnostic and compare directly; an UNMEASURED
 * metric (null, the impression-share family) has nothing to compare either.
 */
export function googleMetricDelta(
  current: GoogleAdsMetrics | undefined,
  previous: GoogleAdsMetrics | undefined,
  key: GoogleAdsMetricKey,
  money?: GoogleMoneyDisplay,
): HeroDelta | null {
  // ⚠️ With a display currency BOTH periods are converted first, so the
  // "single currency both sides" requirement below is satisfied by construction
  // and a mixed-currency scope finally GETS a money delta — the refusal was
  // never about mixing per se, it was about having no single figure.
  if (money && !isNativeDisplay(money.display)) {
    current = convertGoogleMetrics(current, money.display, money.snapshot);
    previous = convertGoogleMetrics(previous, money.display, money.snapshot);
  }
  if (!current || !previous) return null;
  if (isMoneyMetric(key)) {
    const cur = metricMoney(current, key);
    const prev = metricMoney(previous, key);
    if (cur.length !== 1 || prev.length !== 1) return null;
    if (cur[0]!.currency !== prev[0]!.currency) return null;
    return computeDelta(cur[0]!.amount, prev[0]!.amount);
  }
  const cur = metricScalar(current, key);
  const prev = metricScalar(previous, key);
  if (cur === null || prev === null) return null;
  return computeDelta(cur, prev);
}

/**
 * The `TrendMetric` shape the Meta chart components read (they use `label`,
 * `format` and `kind`). Built from the Google catalogue so one metric definition
 * drives the tiles, the table and the charts.
 */
export function googleTrendMetric(key: GoogleAdsMetricKey): TrendMetric {
  const def = metricDef(key);
  const format =
    def?.kind === 'money' ? 'money' : def?.kind === 'pct' ? 'ratio' : ('count' as const);
  return {
    key,
    label: def?.label ?? key,
    format: def?.kind === 'multiple' ? 'count' : format,
    tone: googleMetricTone(key),
    // Nullable (impression-share) metrics gap-fill as null; additive ones as 0.
    kind: def?.nullable || def?.kind === 'pct' || def?.kind === 'multiple' ? 'ratio' : 'additive',
    // The Meta components never call this — Google values come from
    // `computeMetrics` so the per-currency + ratio rules are honoured.
    value: () => 0,
  };
}

/**
 * Shape a Google summary into single-series chart data for one metric.
 *
 * The series spans the FULL calendar domain of the range (one slot per bucket)
 * with the data left-joined on, so a gap stays visible in position rather than
 * being collapsed — the same fill rule every Meta chart uses, reused here so the
 * two dashboards cannot disagree about which buckets they draw.
 */
export function buildGoogleTrendSeries(
  summary: GoogleAdsSummary | undefined,
  key: GoogleAdsMetricKey,
  grouping: TrendGrouping,
  money?: GoogleMoneyDisplay,
): TrendChartData {
  const metric = googleTrendMetric(key);
  const currency = summary ? currencyFor(summary, key, money) : null;
  const base = { metric, format: metric.format, currency, grouping };

  if (!summary || summary.trend.length === 0) {
    return { ...base, rows: [], isEmpty: true };
  }

  const buckets = bucketGoogleTrend(summary.trend, grouping);
  const byBucket = new Map(buckets.map((b) => [b.bucket, b.byCurrency]));
  const domain = enumerateBucketDomain(summary.range.from, summary.range.to, grouping);
  // Defensive: keep any data bucket that falls outside the stated range rather
  // than dropping a server row on the floor.
  for (const b of buckets) if (!domain.includes(b.bucket)) domain.push(b.bucket);
  domain.sort((a, b) => a.localeCompare(b));

  const gap: number | null = metric.kind === 'ratio' ? null : 0;
  const rows = domain.map((bucket) => {
    const totals = byBucket.get(bucket);
    return { bucket, value: totals ? bucketValue(totals, key, currency, money) : gap };
  });

  return { ...base, rows, isEmpty: false };
}

/** Zeroed per-currency totals — exported for tests/fixtures. */
export function emptyBucket(currency = 'AUD'): TotalsByCurrency {
  const m: TotalsByCurrency = new Map();
  addToCurrency(m, currency, emptyTotals() as GoogleAdsTotals);
  return m;
}
